import groupBy from 'lodash/groupBy';
import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import {
  PortfolioSecurityExposureDTO,
  PortfolioDTO,
  PortfolioComponentId,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { Mapper, MapperParams } from '../type.mapper';
import { getFirstNonEmptyProp, parseStdNumber } from '../mapper.utils';

// tslint:disable-next-line:max-line-length
// example fund: https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/18120/A/franklin-k-2-alternative-strategies-fund/FAAAX#portfolio
export class SecurityExposureMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioSecurityExposureDTO[] = portfolioDto.securityexposure;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    // this trims _NET or _GROSS off the end of the std category, so it can be grouped appropriately
    const grouper = (point: PortfolioSecurityExposureDTO): string =>
      point.exposurecategorystd?.replace(/_(NET|GROSS)$/g, '');

    const allocation: PortfolioAllocation = {
      componentId: PortfolioComponentId.SECURITY_TYPE_EXPOSURE,
      baseLabelKey: 'security-type-exposure',
      showExposureChart: true,
      // allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'), // TODO: allocheadinglocal doesn't exist in gql
      asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
      // calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'), // TODO: calcbasislocal doesn't exist in gql
      // calculationType: getFirstNonEmptyProp(data, 'calctypelocal'), // TODO: calctypelocal doesn't exist in gql
      updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
      data: Object.values(groupBy(data, grouper))
        // after grouping, we'll get an array with [gross, net] pairs e.g. [[{gross}, {net}], [{net}, {gross}], [{gross}, {net}], ...]
        .map(
          (
            group: PortfolioSecurityExposureDTO[]
          ): PortfolioAllocationDataPoint => {
            // extract the gross and net data points for this category
            const netPoint: PortfolioSecurityExposureDTO = group.find(
              (point: PortfolioSecurityExposureDTO): boolean =>
                point.exposurecategorystd?.endsWith('_NET')
            );
            const grossPoint: PortfolioSecurityExposureDTO = group.find(
              (point: PortfolioSecurityExposureDTO): boolean =>
                point.exposurecategorystd?.endsWith('_GROSS')
            );
            // return a single point with gross+net values
            return {
              label: group[0].exposurecategory,
              grossExposure: formatPercentage(grossPoint?.brkdwnpct),
              grossExposureStd: parseStdNumber(grossPoint?.brkdwnpctstd),
              netExposure: formatPercentage(netPoint?.brkdwnpct),
              netExposureStd: parseStdNumber(netPoint?.brkdwnpctstd),
              // work out this point's order based on the lowest "rank" property of the category
              order: Math.min(
                parseStdNumber(grossPoint?.rank),
                parseStdNumber(netPoint?.rank)
              ),
            };
          }
        )
        // sort the array
        .sort(
          (
            a: PortfolioAllocationDataPoint,
            b: PortfolioAllocationDataPoint
          ): number => a.order - b.order
        ),
    };
    return [allocation];
  }
}
